import { Axios } from "@/utils/axios"; // 부고 생성 함수
import {
  ObituaryResponse,
  ObituaryRequestBody,
  ObituarySanju,
  ObituarySummary,
  BankAccountResponse,
  BankAccountShareMode,
  AccountDisplayMode,
} from "@/containers/BugoContainer/type";
import { PaginationResponse } from "@/types/apis";

/** isAuthenticated */
export const createObituary = async (body: ObituaryRequestBody) => {
  const { data } = await Axios.post<string>("/obituaries/", body);
  return data;
};

/** isAuthenticated, isOwner */
export const updateObituary = async ({
  key,
  body,
}: {
  key: ObituaryResponse["key"];
  body: ObituaryRequestBody;
}) => {
  const { data } = await Axios.put<string>(`/obituaries/${key}/`, body);
  return data;
};

/** isAuthenticated, isOwner — 부분 수정 (portrait 등) */
export const patchObituary = async ({
  key,
  body,
}: {
  key: ObituaryResponse["key"];
  body: Partial<ObituaryRequestBody>;
}) => {
  const { data } = await Axios.patch<string>(`/obituaries/${key}/`, body);
  return data;
};

/** 영정사진 업로드 → S3 키 반환 */
export const uploadPortrait = async ({
  key,
  body,
}: {
  key: ObituaryResponse["key"];
  body: { data: string; file_name: string };
}) => {
  const { data } = await Axios.post<string>(
    `/obituaries/${key}/portrait/`,
    body
  );
  return data;
};

export const getObituaries = async (key: ObituaryResponse["key"]) => {
  const { data } = await Axios.get<ObituaryResponse>(`/obituaries/${key}/`);
  return data;
};

export const deleteObituary = async (key: ObituaryResponse["key"]) => {
  const { data } = await Axios.delete(`/obituaries/${key}/`);
  return data;
};

export const shareObituaryToSangju = async (
  key: ObituaryResponse["key"],
  body: { sangju: ObituarySanju["key"] }
) => {
  const { data } = await Axios.post(
    `/obituaries/${key}/sangju_invitation/`,
    body
  );
  return data;
};

export const getObituarySummaryList = async () => {
  const { data } = await Axios.get<PaginationResponse<ObituarySummary>>(
    "/obituaries/"
  );
  return data;
};

export type ObituarySangjuVersionResponse = {
  key: string;
  name: string;
  relation: string;
  phone: string;
  obituary: ObituaryResponse;
  bank_account: BankAccountResponse;
  bank_account_share_mode: BankAccountShareMode | null;
};

export const getObituariesSangjuVersion = async (
  authentication_key: string
) => {
  const { data } = await Axios.get<ObituarySangjuVersionResponse>(
    `/authenticated_obituary_sangjus/${authentication_key}/`
  );
  return data;
};

export type ObituaryVisitorVersionResponse = {
  key: string;
  name: string;
  relation: string;
  phone: string;
  account_display_mode?: AccountDisplayMode;
  obituary: ObituaryResponse;
  bank_accounts: BankAccountResponse[];
  /**
   * 발인 + 7일 게이트 + 임시 키 만료 여부를 백엔드가 단일 계산해 내려주는 플래그.
   * 프론트는 이 값으로만 만료 화면 분기 (시각 비교 직접 수행 X).
   */
  is_expired?: boolean;
  /**
   * 유효한 임시 키로 접근한 경우 그 키의 만료 시각 (ISO 8601 + offset).
   * 일반 접근이거나 무효한 키면 null.
   */
  temporary_access_expires_at?: string | null;
};

export const getObituariesVisitorVersion = async (
  key: ObituaryResponse["sangjus"][0]["key"],
  temporaryKey?: string
) => {
  const { data } = await Axios.get<ObituaryVisitorVersionResponse>(
    `/obituary_sangjus/${key}/`,
    {
      params: temporaryKey ? { temporary_key: temporaryKey } : undefined,
    }
  );
  return data;
};

export type WreathOrderItem = {
  masked_name: string;
  created_at: string;
  item_name: string;
};

export type WreathOrdersResponse = {
  count: number;
  orders: WreathOrderItem[];
};

export const updateSangjuBankAccountShareMode = async (
  authenticationKey: string,
  bank_account_share_mode: BankAccountShareMode
) => {
  const { data } = await Axios.patch(
    `/authenticated_obituary_sangjus/${authenticationKey}/`,
    { bank_account_share_mode }
  );
  return data;
};

/** isAuthenticated, isOwner — 부고 사용 불가 사유 제출 */
export const submitUsageException = async (
  key: ObituaryResponse["key"],
  body: { reason: string; note?: string }
) => {
  const { data } = await Axios.post(
    `/obituaries/${key}/usage_exception/`,
    body
  );
  return data;
};

export const getWreathOrders = async (
  sangjuKey: ObituaryResponse["sangjus"][0]["key"]
) => {
  const { data } = await Axios.get<WreathOrdersResponse>(
    `/obituary_sangjus/${sangjuKey}/wreath_orders/`
  );
  return data;
};
